import * as pako from "pako";
import { CDN } from "../config/cdn";

const PREFIX = "TikTok_Dashboard";
const manifestUrl = `${CDN.tiktok}/${PREFIX}/manifest.json`;
let current = null;
let checkedAt = 0;
let pending = null;
const histories = new Map();

async function json(url) {
  const response = await fetch(url, { cache: url === manifestUrl ? "no-cache" : "default" });
  if (!response.ok) throw new Error(`TikTok export unavailable (HTTP ${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  return JSON.parse(bytes[0] === 31 && bytes[1] === 139
    ? pako.ungzip(bytes, { to: "string" }) : new TextDecoder().decode(bytes));
}

export function validTikTokHistoryKey(key) {
  return typeof key === "string" && /^TikTok_Dashboard\/histories\/[1-9]\d*\/[a-f0-9]{64}\.json\.gz$/.test(key);
}

export function tikTokHistoryKey(game, period = "30d") {
  return period === "90d" ? game.daily_history_key : game.history_key;
}

export function loadTikTokDataset({ force = false } = {}) {
  if (pending) return pending;
  if (!force && current && Date.now() - checkedAt < 60000) return Promise.resolve(current);
  pending = (async () => {
    const manifest = await json(manifestUrl);
    if (manifest.schema_version !== 1 || manifest.source !== "mindgame_tiktok" ||
        !/^[a-f0-9]{64}$/.test(manifest.revision) ||
        manifest.ranking_key !== `${PREFIX}/revisions/${manifest.revision}/ranking.json.gz` ||
        !Number.isFinite(Date.parse(manifest.generated_at)) ||
        !Number.isFinite(Date.parse(manifest.latest_source_dt)) ||
        !Number.isSafeInteger(manifest.row_count) || manifest.row_count <= 0 ||
        ![30, 90, 180].includes(manifest.history_days) ||
        (manifest.history_days === 30 && (manifest.daily_history_days !== 90 ||
          manifest.history_layout !== "observations_30_daily_net_90"))) throw new Error("Invalid current TikTok manifest");
    if (current?.manifest.revision !== manifest.revision) {
      const dataset = await json(`${CDN.tiktok}/${manifest.ranking_key}`);
      if (dataset.schema_version !== 1 || dataset.source !== "mindgame_tiktok" ||
          !Array.isArray(dataset.rows) || dataset.rows.length !== manifest.row_count ||
          new Set(dataset.rows.map(row => row.config_id)).size !== dataset.rows.length ||
          dataset.rows.some(row => !Number.isSafeInteger(row.config_id) ||
            !validTikTokHistoryKey(row.history_key) ||
            !row.history_key.startsWith(`${PREFIX}/histories/${row.config_id}/`) ||
            (manifest.history_days === 30 && (!validTikTokHistoryKey(row.daily_history_key) ||
              !row.daily_history_key.startsWith(`${PREFIX}/histories/${row.config_id}/`))) ||
            !Number.isFinite(row.views) || row.views < 0 || !Number.isFinite(row.videos) || row.videos < 0 ||
            !Number.isFinite(Date.parse(row.dt)) ||
            ["1d", "7d"].some(window => {
              const metric = row[`window_${window}`];
              return !metric || typeof metric.available !== "boolean" ||
                ["views", "videos"].some(key => metric[key] != null && (!Number.isFinite(metric[key]) || metric[key] < 0)) ||
                (metric.available && metric.views == null && metric.videos == null);
            }))) {
        throw new Error("Invalid current TikTok ranking export");
      }
      current = { manifest, rows: dataset.rows };
    }
    current.manifest = manifest;
    checkedAt = Date.now();
    return current;
  })().finally(() => { pending = null; });
  return pending;
}

export function loadTikTokHistory(game, period = "30d") {
  const key = tikTokHistoryKey(game, period);
  if (!validTikTokHistoryKey(key)) return Promise.reject(new Error(
    period === "90d" ? "Daily TikTok history is not available in this publication" : "Invalid current TikTok history reference"));
  if (!key.startsWith(`${PREFIX}/histories/${game.config_id}/`)) {
    return Promise.reject(new Error("Invalid current TikTok history identity"));
  }
  if (!histories.has(key)) {
    const request = json(`${CDN.tiktok}/${key}`).then(payload => {
      const observations = payload.granularity === "observations";
      const dailyNet = payload.granularity === "daily_net";
      const points = payload.points;
      if (payload.source !== "mindgame_tiktok" || payload.schema_version !== 1 ||
          payload.config_id !== Number(game.config_id) || !Array.isArray(points) ||
          (!points.length && !(observations && payload.history_days === 30)) ||
          (observations ? ![30, 90].includes(payload.history_days) :
            payload.history_days !== (dailyNet ? 90 : 180)) ||
          (dailyNet !== (period === "90d")) ||
          (!observations && ((!dailyNet && payload.granularity !== "observed_daily") || points.length > payload.history_days ||
            new Set(points.map(point => point.date)).size !== points.length)) ||
          ((observations || dailyNet) && new Set(points.map(point => point.snapshot_id)).size !== points.length) ||
          (points.length > 0 && Date.parse(points[points.length - 1].date) - Date.parse(points[0].date) >= payload.history_days * 86400000) ||
          points.some((point, index) =>
            !/^\d{4}-\d{2}-\d{2}$/.test(point.date) ||
            !Number.isFinite(Date.parse(point.date)) ||
            !Number.isFinite(Date.parse(point.dt)) ||
            !Number.isFinite(point.views) || point.views < 0 || !Number.isFinite(point.videos) || point.videos < 0 ||
            ((observations || dailyNet) && (!Number.isSafeInteger(point.snapshot_id) || point.snapshot_id <= 0)) ||
            (dailyNet && (!Number.isFinite(Date.parse(point.period_start)) ||
              typeof point.is_partial !== "boolean" ||
              ["delta_views", "delta_videos"].some(metric =>
                point[metric] !== null && !Number.isSafeInteger(point[metric])) ||
              (point.baseline_dt !== null && !Number.isFinite(Date.parse(point.baseline_dt))))) ||
            (index > 0 && (point.date < points[index - 1].date ||
              Date.parse(point.dt) < Date.parse(points[index - 1].dt) ||
              (Date.parse(point.dt) === Date.parse(points[index - 1].dt) &&
                (!observations || point.snapshot_id <= points[index - 1].snapshot_id)))))) {
        throw new Error("Invalid current TikTok history export");
      }
      return payload;
    }).catch(error => { histories.delete(key); throw error; });
    histories.set(key, request);
    if (histories.size > 100) histories.delete(histories.keys().next().value);
  }
  return histories.get(key);
}

export function tikTokMetric(game, window = "latest", metric = "views") {
  if (window === "latest") return game[metric] != null && Number.isFinite(Number(game[metric])) ? Number(game[metric]) : null;
  const details = game[`window_${window}`];
  return details?.available === true && Number.isFinite(details[metric]) ? details[metric] : null;
}

export function tikTokGameGroupKey(game) {
  const gameKey = String(game.gameKey ?? game.game_key ?? game.Game_Key ?? game.name ?? game.game ?? "")
    .trim().toLowerCase();
  if (gameKey) return `game:${gameKey}`;
  const rowKey = game.selectionKey ?? game.config_id ?? game.id;
  return rowKey == null ? null : `row:${rowKey}`;
}

export function selectTikTokComparisonRows(games, comparisonGames) {
  const selected = new Set(comparisonGames || []);
  const selectedGroups = new Set(
    games
      .filter(game => selected.has(game.selectionKey) || selected.has(game.name))
      .map(tikTokGameGroupKey)
      .filter(Boolean)
  );
  return games.filter(game => selectedGroups.has(tikTokGameGroupKey(game)));
}

export function rankTikTokRows(games, window, metric) {
  const dataMetric = metric === "videos" ? "videos" : "views";
  const ranked = games.filter(game => tikTokMetric(game, window, dataMetric) != null)
    .map(game => ({
      ...game, rankedMetrics: true, rankingMetric: dataMetric, viewsTotal: tikTokMetric(game, window, "views"),
      videosTotal: tikTokMetric(game, window, "videos"),
      metricLabel: window === "latest" ? "" : window === "1d" ? "1D" : "7D",
      coverageLabel: window === "7d" && game.window_7d?.partial
        ? `${game.window_7d.observed_dates} dates tracked · partial 7D` : "",
    }))
    .sort((a, b) => {
      if (metric === "momentum") {
        const rankDifference = (a.momentumRank ?? Infinity) - (b.momentumRank ?? Infinity);
        if (rankDifference) return rankDifference;
      }
      return tikTokMetric(b, window, dataMetric) - tikTokMetric(a, window, dataMetric) ||
        Number(a.config_id) - Number(b.config_id);
    });
  const seenGroups = new Set();
  return ranked.filter(game => {
    const groupKey = tikTokGameGroupKey(game);
    if (!groupKey) return true;
    if (seenGroups.has(groupKey)) return false;
    seenGroups.add(groupKey);
    return true;
  });
}
